async function api(path: string, init?: RequestInit): Promise<Response> {
  const res = await fetch(path, { credentials: 'include', ...init });
  if (!res.ok) throw new Error(`${res.status} ${await res.text()}`);
  return res;
}

export async function getJson<T>(path: string): Promise<T> {
  return (await api(path)).json() as Promise<T>;
}

export async function postJson<T>(path: string, body?: unknown): Promise<T> {
  return (
    await api(path, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body ?? {}),
    })
  ).json() as Promise<T>;
}

export async function putJson<T>(path: string, body?: unknown): Promise<T> {
  return (
    await api(path, {
      method: 'PUT',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body ?? {}),
    })
  ).json() as Promise<T>;
}

export async function patchJson<T>(path: string, body?: unknown): Promise<T> {
  return (
    await api(path, {
      method: 'PATCH',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body ?? {}),
    })
  ).json() as Promise<T>;
}

export async function deleteJson<T>(path: string): Promise<T> {
  return (await api(path, { method: 'DELETE' })).json() as Promise<T>;
}

export async function uploadRaw(path: string, bytes: ArrayBuffer, contentType: string): Promise<unknown> {
  return (await api(path, { method: 'POST', headers: { 'content-type': contentType }, body: bytes })).json();
}

export interface Me {
  user: { id: string; email: string; name: string | null; locale: string; is_platform_admin: boolean };
  memberships: Array<{ tenantId: string; role: string; tenantName: string; tenantStatus: string }>;
}

export interface AuthOptions {
  devLogin: boolean;
  mailEnabled: boolean;
  loginTokenTtlMinutes: number;
}

export interface MapSummary {
  id: string;
  name: string;
  currentRevisionId: number | null;
  tenantId: string;
  tenantName: string;
  role: string;
}

export interface Feature {
  type: 'Feature';
  id: string;
  geometry: { type: string; coordinates: unknown };
  properties: Record<string, unknown>;
}

export interface FeatureCollection {
  type: 'FeatureCollection';
  features: Feature[];
}

export type WarningSeverity = 'info' | 'attention' | 'action';

export interface Warning {
  code: string;
  severity: WarningSeverity;
  params: Record<string, number | string>;
}

export interface ProposalSummary {
  counts: { add: number; update: number; delete: number };
  possibleMoves: number;
  duplicates: number;
  driftGroups: number;
  removalGroups: number;
  /** Structured warnings; a plain string is a legacy proposal summary. */
  warnings: Array<Warning | string>;
}

export interface Proposal {
  id: string;
  title: string;
  status: string;
  sourceKind: string;
  authorId: string | null;
  baseRevisionId: number | null;
  mergedRevisionId: number | null;
  createdAt: string;
  summary: ProposalSummary | null;
}

export interface ProposalDetail extends Proposal {
  mergedRevisionNumber: number | null;
  resolvedAt: string | null;
  counts: { total: number; pending: number; accepted: number; rejected: number };
  approvals: { approve: number; counted: number; reject: number; required: number; quorumMet: boolean };
}

export type Decision = 'pending' | 'accepted' | 'rejected';

export interface ChangeItem {
  itemKey: string;
  action: 'add' | 'update' | 'delete';
  featureId: string | null;
  before: { geometry?: unknown; properties?: Record<string, unknown> } | null;
  after: { geometry?: unknown; properties?: Record<string, unknown> } | null;
  possibleMoveOf: string | null;
  candidateOf: string | null;
  ambiguity: string | null;
  driftGroup: string | null;
  removalGroup: string | null;
  decision: Decision;
  decisionComment: string | null;
}

export interface Revision {
  id: number;
  number: number;
  parentRevisionId: number | null;
  artifactHash: string;
  authorId: string | null;
  authorEmail: string | null;
  message: string | null;
  summary: unknown;
  createdAt: string;
}

export interface AppNotification {
  id: string;
  kind: string;
  title: string | null;
  body: unknown;
  proposalId: string | null;
  revisionId: number | null;
  readAt: string | null;
  createdAt: string;
}

export interface Comment {
  id: string;
  proposalId: string;
  itemKey: string | null;
  authorId: string;
  authorEmail: string | null;
  body: string;
  createdAt: string;
  orphaned: boolean;
}

export interface FeatureHistoryEntry {
  revisionId: number;
  revisionNumber: number;
  createdAt: string;
  authorEmail: string | null;
  message: string | null;
  change: 'added' | 'updated' | 'removed';
}

export interface ImportProposalResult {
  proposalId: string | null;
  baseRevisionId: number | null;
  summary: ProposalSummary;
  noChanges?: boolean;
}

export interface Member {
  userId: string;
  email: string;
  name: string | null;
  role: string;
  createdAt: string;
  lastLoginAt: string | null;
  deactivatedAt: string | null;
}

export interface Invitation {
  id: string;
  email: string;
  role: string;
  expiresAt: string;
  createdAt: string;
  lastSentAt: string;
  createdByEmail: string | null;
}

export interface TypeCatalogEntry {
  id: string;
  source: string;
  value: string;
  labelSv: string | null;
  labelEn: string | null;
  descriptionSv: string | null;
  descriptionEn: string | null;
  sort: number;
}

export interface AdminTenant {
  id: string;
  name: string;
  status: string;
  createdAt: string;
  archivedAt: string | null;
  suspendedAt: string | null;
  memberCount: number;
  mapCount: number;
  adminEmail: string | null;
}

export interface AdminUser {
  id: string;
  email: string;
  name: string | null;
  isPlatformAdmin: boolean;
  deactivatedAt: string | null;
  createdAt: string;
  lastLoginAt: string | null;
  memberships: Array<{ tenantId: string; role: string; tenantName: string; tenantStatus: string }>;
}

export const getProposals = (tenantId: string, mapId: string) =>
  getJson<{ proposals: Proposal[] }>(`/api/v1/tenants/${tenantId}/maps/${mapId}/proposals`);

export const getProposalChanges = (tenantId: string, mapId: string, proposalId: string) =>
  getJson<{ changes: ChangeItem[] }>(
    `/api/v1/tenants/${tenantId}/maps/${mapId}/proposals/${proposalId}/changes`,
  );

export const getProposalDetail = (tenantId: string, mapId: string, proposalId: string) =>
  getJson<ProposalDetail>(`/api/v1/tenants/${tenantId}/maps/${mapId}/proposals/${proposalId}`);

export const getRevisions = (tenantId: string, mapId: string) =>
  getJson<{ revisions: Revision[] }>(`/api/v1/tenants/${tenantId}/maps/${mapId}/revisions`);

export const getNotifications = (tenantId: string) =>
  getJson<{ notifications: AppNotification[] }>(`/api/v1/tenants/${tenantId}/notifications`);

export const getComments = (tenantId: string, mapId: string, proposalId: string) =>
  getJson<{ comments: Comment[] }>(
    `/api/v1/tenants/${tenantId}/maps/${mapId}/proposals/${proposalId}/comments`,
  );

export const getFeatureHistory = (tenantId: string, mapId: string, featureId: string) =>
  getJson<{ featureId: string; history: FeatureHistoryEntry[] }>(
    `/api/v1/tenants/${tenantId}/maps/${mapId}/features/${encodeURIComponent(featureId)}/history`,
  );

export const createMap = (tenantId: string, name: string) =>
  postJson<{ mapId: string }>(`/api/v1/tenants/${tenantId}/maps`, { name });

export const deleteMap = (tenantId: string, mapId: string) =>
  deleteJson<{ ok: boolean }>(`/api/v1/tenants/${tenantId}/maps/${mapId}`);

export interface MapSettings {
  identityStrategy: string[];
  moveToleranceM: number;
  geometryEqToleranceM: number;
  region: unknown;
  requiredApprovals: number;
}

export const getMapSettings = (tenantId: string, mapId: string) =>
  getJson<MapSettings>(`/api/v1/tenants/${tenantId}/maps/${mapId}/settings`);

export const putMapSettings = (tenantId: string, mapId: string, body: Partial<MapSettings>) =>
  putJson<{ ok: boolean; recomputed: number; invalidated: number }>(
    `/api/v1/tenants/${tenantId}/maps/${mapId}/settings`,
    body,
  );

export const reindexMap = (tenantId: string, mapId: string) =>
  postJson<{ features: number }>(`/api/v1/tenants/${tenantId}/maps/${mapId}/reindex`, {});

export interface ChainVerification {
  ok: boolean;
  checked: number;
  legacy: number;
  brokenAt: number | null;
}

export const verifyChain = (tenantId: string, mapId: string) =>
  getJson<ChainVerification>(`/api/v1/tenants/${tenantId}/maps/${mapId}/verify`);

export const rebaseProposal = (tenantId: string, mapId: string, proposalId: string) =>
  postJson<{ invalidated: number; dropped: number }>(
    `/api/v1/tenants/${tenantId}/maps/${mapId}/proposals/${proposalId}/rebase?force=1`,
    {},
  );

export type EditOperation =
  | { op: 'upsert'; feature: Feature }
  | { op: 'delete'; id: string };

export const createEditProposal = (
  tenantId: string,
  mapId: string,
  operations: EditOperation[],
  title?: string,
) =>
  postJson<{
    proposalId: string | null;
    baseRevisionId: number | null;
    summary: ProposalSummary;
    noChanges?: boolean;
  }>(`/api/v1/tenants/${tenantId}/maps/${mapId}/edits`, { operations, ...(title ? { title } : {}) });

export const updateEditProposal = (
  tenantId: string,
  mapId: string,
  proposalId: string,
  operations: EditOperation[],
) =>
  putJson<{ proposalId: string; summary: ProposalSummary; noChanges?: boolean }>(
    `/api/v1/tenants/${tenantId}/maps/${mapId}/proposals/${proposalId}/edits`,
    { operations },
  );

export const getProposedFeatures = (tenantId: string, mapId: string, proposalId: string) =>
  getJson<FeatureCollection>(
    `/api/v1/tenants/${tenantId}/maps/${mapId}/proposals/${proposalId}/proposed`,
  );

export const acceptInvite = (token: string) =>
  postJson<{ ok: boolean; tenantId: string; role: string }>('/api/v1/auth/accept-invite', { token });

export const getTypeCatalog = () => getJson<{ types: TypeCatalogEntry[] }>('/api/v1/types');

export const createAdminType = (
  input: Partial<Omit<TypeCatalogEntry, 'id'>> & { source: string; value: string },
) => postJson<TypeCatalogEntry>('/api/v1/admin/types', input);

export const updateAdminType = (id: string, input: Partial<Omit<TypeCatalogEntry, 'id'>>) =>
  patchJson<TypeCatalogEntry>(`/api/v1/admin/types/${id}`, input);

export const deleteAdminType = (id: string) =>
  deleteJson<{ ok: boolean }>(`/api/v1/admin/types/${id}`);

export const renameTenant = (tenantId: string, name: string) =>
  patchJson<{ name: string }>(`/api/v1/tenants/${tenantId}`, { name });

export const getMembers = (tenantId: string) =>
  getJson<{ members: Member[]; invitations: Invitation[] }>(`/api/v1/tenants/${tenantId}/members`);

export const inviteMember = (tenantId: string, email: string, role: string) =>
  postJson<{ invitationId: string; token: string; expiresAt: string; link: string; emailQueued: boolean }>(
    `/api/v1/tenants/${tenantId}/invitations`,
    { email, role },
  );

export const revokeInvitation = (tenantId: string, invitationId: string) =>
  deleteJson<{ ok: boolean }>(`/api/v1/tenants/${tenantId}/invitations/${invitationId}`);

export const resendInvitation = (tenantId: string, invitationId: string) =>
  postJson<{ token: string; expiresAt: string; link: string; emailQueued: boolean }>(
    `/api/v1/tenants/${tenantId}/invitations/${invitationId}/resend`,
    {},
  );

export const changeMemberRole = (tenantId: string, userId: string, role: string) =>
  patchJson<{ role: string }>(`/api/v1/tenants/${tenantId}/members/${userId}`, { role });

export const removeMember = (tenantId: string, userId: string) =>
  deleteJson<{ ok: boolean }>(`/api/v1/tenants/${tenantId}/members/${userId}`);

export const renameMap = (tenantId: string, mapId: string, name: string) =>
  patchJson<{ name: string }>(`/api/v1/tenants/${tenantId}/maps/${mapId}`, { name });

export const getAdminTenants = () => getJson<{ tenants: AdminTenant[] }>('/api/v1/admin/tenants');

export const createAdminTenant = (name: string, adminEmail: string) =>
  postJson<{ tenantId: string; invitationId: string; link: string; emailQueued: boolean }>(
    '/api/v1/admin/tenants',
    {
      name,
      adminEmail,
    },
  );

export const setAdminTenantStatus = (tenantId: string, status: 'active' | 'suspended' | 'archived') =>
  patchJson<{ status: string }>(`/api/v1/admin/tenants/${tenantId}/status`, { status });

export const changeAdmin = (tenantId: string, email: string) =>
  postJson<{ userId: string; link: string; emailQueued: boolean }>(
    `/api/v1/admin/tenants/${tenantId}/change-admin`,
    { email },
  );

export const getAdminUsers = (includeInactive: boolean) =>
  getJson<{ users: AdminUser[] }>(`/api/v1/admin/users${includeInactive ? '?includeInactive=1' : ''}`);

export const setAdminUserDeactivated = (userId: string, deactivated: boolean) =>
  patchJson<{ deactivated: boolean }>(`/api/v1/admin/users/${userId}`, { deactivated });

export const deleteAdminUser = (userId: string) =>
  deleteJson<{ ok: boolean }>(`/api/v1/admin/users/${userId}`);
